import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../utils/api';

const TaskDocuments = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [selectedTaskFilter, setSelectedTaskFilter] = useState('all');

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await api.get(
        `${process.env.REACT_APP_BACKEND_API_URL}/admin-tasks/documents`
      );
      if (response.ok) {
        const data = await response.json();
        setDocuments(data.documents || []);
      }
    } catch (error) {
      console.error('Error fetching task documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDocument = async (doc) => {
    if (!window.confirm(`Are you sure you want to delete "${doc.fileName}"?`)) {
      return;
    }

    try {
      setDeleting(doc.fileName);
      const response = await api.delete(
        `${process.env.REACT_APP_BACKEND_API_URL}/admin-tasks/documents/${doc.taskId}/${encodeURIComponent(doc.fileName)}`
      );
      if (response.ok) {
        setDocuments(prev => prev.filter(d => !(d.taskId === doc.taskId && d.fileName === doc.fileName)));
        alert('Document deleted successfully');
      } else {
        alert('Failed to delete document');
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Failed to delete document');
    } finally {
      setDeleting(null);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const size = parseInt(bytes);
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatPeriodKey = (periodKey) => {
    if (!periodKey) return 'N/A';
    // Format "2025-01" to "January 2025"
    const parts = periodKey.split('-');
    if (parts.length === 2) {
      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                          'July', 'August', 'September', 'October', 'November', 'December'];
      const monthIndex = parseInt(parts[1], 10) - 1;
      if (monthIndex >= 0 && monthIndex < 12) {
        return `${monthNames[monthIndex]} ${parts[0]}`;
      }
    }
    return periodKey;
  };

  // Get unique task titles for filter dropdown
  const taskTitles = useMemo(() => {
    const titles = [...new Set(documents.map(doc => doc.taskTitle).filter(Boolean))];
    return titles.sort();
  }, [documents]);

  // Filter documents based on selected task
  const filteredDocuments = useMemo(() => {
    if (selectedTaskFilter === 'all') return documents;
    return documents.filter(doc => doc.taskTitle === selectedTaskFilter);
  }, [documents, selectedTaskFilter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading documents...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Task Documents</h1>
        <p className="text-gray-600 mt-2">All files uploaded for admin tasks</p>
      </div>

      {/* Task Filter */}
      {taskTitles.length > 0 && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Task</label>
          <select
            value={selectedTaskFilter}
            onChange={(e) => setSelectedTaskFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-64"
          >
            <option value="all">All Tasks</option>
            {taskTitles.map(title => (
              <option key={title} value={title}>{title}</option>
            ))}
          </select>
        </div>
      )}

      <div className="card overflow-hidden">
        {filteredDocuments.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No documents uploaded yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Task</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">File Label</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">File Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Uploaded Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Task Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Size</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredDocuments.map((doc, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{doc.taskTitle}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{doc.uploadItemName || 'N/A'}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{doc.fileName}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {doc.completedAt ? new Date(doc.completedAt).toLocaleDateString() : formatPeriodKey(doc.periodKey)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{formatFileSize(doc.size)}</td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex items-center justify-center gap-3">
                        <a
                          href={`${process.env.REACT_APP_BACKEND_API_URL}/admin-tasks/documents/${doc.taskId}/${encodeURIComponent(doc.fileName)}/view`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                        >
                          Download
                        </a>
                        <button
                          onClick={() => handleDeleteDocument(doc)}
                          disabled={deleting === doc.fileName}
                          className="text-red-600 hover:text-red-700 text-sm font-medium disabled:opacity-50"
                        >
                          {deleting === doc.fileName ? 'Deleting...' : 'Delete'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskDocuments;
